import { Download } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
} from '@mui/material';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { ActiveMoneyReportItem } from 'lib/api/reports/types';
import { DateInput, SelectInput } from 'lib/common/Inputs';
import CustomButton from 'lib/designSystem/Button';
import PageHeader from 'lib/designSystem/PageHeader';
import EnhancedTable from 'lib/designSystem/Table';
import { daysBetween } from 'lib/functions/dates';
import { formatDate } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';

type DebtProjectorProps = {
  open: boolean;
  documents: ActiveMoneyReportItem[];
  setOpen: (open: boolean) => void;
};

const excelHeaders = [
  'Rut cliente',
  'Nombre cliente',
  'Fecha operación',
  'Número documento',
  'Saldo documento',
  'Saldo documento neto',
  'Fecha vencimiento',
  'Fecha último abono',
  'Fecha proyectada de pago',
  'Tasa mora (%)',
  'Días mora',
  'Mora proyectada',
];

const DebtProjector: React.FC<DebtProjectorProps> = (props) => {
  const { setOpen } = props;
  const [documents, setDocuments] = useState<
    (ActiveMoneyReportItem & { newPaymentDate?: string; arrear?: number })[]
  >(props.documents);
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentDateType, setPaymentDateType] = useState(
    'lastConciliationDate',
  );
  const [amountType, setAmountType] = useState('documentRemainingAmount');
  const getAmount = (item: ActiveMoneyReportItem) => {
    if (amountType === 'documentRemainingAmount') {
      return item.documentRemainingAmount;
    } else if (amountType === 'documentRemainingAmountNet') {
      return item.documentRemainingAmountNet;
    } else if (amountType === 'documentAmount') {
      return item.amount;
    } else {
      return 0;
    }
  };
  const exportToExcel = async () => {
    const asValidDate = (value?: string | Date) => {
      if (!value) return null;
      const date = value instanceof Date ? value : new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const plainData = documents.map((item) => {
      return [
        item.businessIdentifier,
        item.businessName,
        asValidDate(item.operationDate),
        item.documentNumber,
        item.documentRemainingAmount,
        item.documentRemainingAmountNet,
        asValidDate(item.dueDate),
        asValidDate(item.lastConciliationDate),
        paymentDate
          ? paymentDate.toDate()
          : (asValidDate(item.newPaymentDate) ?? ''),
        item.arrearRate,
        item.arrearDays,
        item.arrear,
      ];
    });
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Ensure date columns are treated as dates in Excel
    worksheet.getColumn(3).numFmt = 'dd/mm/yyyy'; // Fecha operación
    worksheet.getColumn(7).numFmt = 'dd/mm/yyyy'; // Fecha vencimiento
    worksheet.getColumn(8).numFmt = 'dd/mm/yyyy'; // Fecha último abono
    worksheet.getColumn(9).numFmt = 'dd/mm/yyyy'; // Fecha proyectada de pago

    // Add data to worksheet
    [excelHeaders, ...plainData].forEach((rowData, rowIndex) => {
      const row = worksheet.addRow(rowData);
      if (rowIndex === 0) {
        // Make the first row bold, visible, and add grey background color
        row.eachCell((cell) => {
          cell.font = { bold: true };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          worksheet.getColumn(cell.col).width = 20;
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFDDDDDD' }, // Grey background color
          };
        });
      }
    });
    // Save workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `Proyección_Moras_${formatDate(paymentDate.toDate())}.xlsx`;
    a.click();

    URL.revokeObjectURL(url);
  };

  const calculateArrear = () => {
    const newDocuments = [];
    let documentsToReview;
    if (documents.length === 0) {
      documentsToReview = props.documents;
    } else {
      documentsToReview = documents;
    }
    for (const document of documentsToReview) {
      const startDate = new Date(
        paymentDateType === 'lastConciliationDate'
          ? document.lastConciliationDate
          : document.dueDate,
      ).setUTCHours(12, 0, 0, 0);

      const endDate = paymentDate.toDate().setUTCHours(12, 0, 0, 0);

      const days = daysBetween(startDate, endDate);
      const daysReal = days > 0 ? days : 0;
      const amount = getAmount(document);
      const arrear =
        ((amount * Number(document.arrearRate)) / 100 / 30) * daysReal;
      const newDocument = {
        ...document,
        arrearDays: daysReal,
        arrear: Math.round(arrear),
        newPaymentDate: formatDate(paymentDate.toDate()),
      };
      newDocuments.push(newDocument);
    }
    setDocuments(newDocuments);
  };

  const handleEditArrearRate = (
    rowId: string | number,
    value: number | null,
  ) => {
    if (value === null) return;

    setDocuments((prev) =>
      prev.map((document) => {
        if (String(document.id) !== String(rowId)) {
          return document;
        }

        const arrearRate = value;
        const days = dayjs(paymentDate).diff(
          dayjs(
            paymentDateType === 'lastConciliationDate'
              ? document.lastConciliationDate
              : document.dueDate,
          ),
          'day',
        );
        const daysReal = days > 0 ? days : 0;
        const amount = getAmount(document);
        const arrear = ((amount * arrearRate) / 100 / 30) * daysReal;
        return {
          ...document,
          arrearRate,
          arrearDays: daysReal,
          arrear: Math.round(arrear),
          newPaymentDate: formatDate(paymentDate.toDate()),
        };
      }),
    );
  };

  const getAmountHeaderObject = () => {
    if (amountType === 'documentRemainingAmountNet') {
      return {
        key: 'documentRemainingAmountNet',
        label: 'Monto a proyectar',
        type: 'money',
      };
    } else if (amountType === 'documentRemainingAmount') {
      return {
        key: 'documentRemainingAmount',
        label: 'Monto a proyectar',
        type: 'money',
      };
    } else {
      return {
        key: 'amount',
        label: 'Monto a proyectar',
        type: 'money',
      };
    }
  };

  const headers = [
    {
      key: 'businessIdentifier',
      label: 'Rut cliente',
      type: 'report',
    },
    { key: 'businessName', label: 'Nombre cliente' },
    {
      key: 'operationDate',
      label: 'Fecha operación',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    { key: 'documentNumber', label: 'Número documento' },
    { key: 'amount', label: 'Monto Doc.', type: 'money' },
    { key: 'documentRemainingAmount', label: 'Saldo doc.', type: 'money' },
    {
      key: 'documentRemainingAmountNet',
      label: 'Saldo doc. neto',
      type: 'money',
    },
    {
      key: 'dueDate',
      label: 'Vencimiento',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'lastConciliationDate',
      label: 'Último abono',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'newPaymentDate',
      label: 'Fecha proyectada de pago',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'arrearRate',
      label: 'Tasa mora (%)',
      minWidth: '100px',
      type: 'editable-arrear-rate',
      customAction: handleEditArrearRate,
    },
    { key: 'arrearDays', label: 'Días mora' },
    ...[getAmountHeaderObject()],
    { key: 'arrear', label: 'Mora proyectada', type: 'money' },
  ];

  useEffect(() => {
    calculateArrear();
  }, [paymentDate, props.documents, paymentDateType, amountType]);

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={() => props.setOpen(false)}
        fullWidth={true}
        maxWidth="xl"
      >
        <DialogContent>
          <div>
            <PageHeader variant="dialog">
              <PageHeader.Main>
                <PageHeader.TitleSection
                  iconType="insights"
                  title="Proyectar mora"
                  subtitle="Selecciona los parámetros y descarga el reporte en formato Excel."
                />
                <PageHeader.ActionButtons
                  buttons={[
                    {
                      label: 'Descargar XLSX',
                      onClick: () => void exportToExcel(),
                      color: 'primary',
                      variant: 'contained',
                      startIcon: <Download />,
                    },
                  ]}
                />
              </PageHeader.Main>
              <Divider sx={{ my: 1 }} />
              <PageHeader.ActionsBar>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <SelectInput
                    label="Fecha inicio"
                    value={paymentDateType}
                    onChange={(value) => {
                      if (value) {
                        setPaymentDateType(value);
                      }
                    }}
                    options={[
                      { value: 'lastConciliationDate', label: 'Último abono' },
                      { value: 'dueDate', label: 'Vencimiento' },
                    ]}
                  />
                  <SelectInput
                    label="Monto referencia"
                    value={amountType}
                    onChange={(value) => {
                      if (value) {
                        setAmountType(value);
                      }
                    }}
                    options={[
                      {
                        value: 'documentRemainingAmountNet',
                        label: 'Saldo anticipado',
                      },
                      {
                        value: 'documentRemainingAmount',
                        label: 'Saldo documento',
                      },
                      {
                        value: 'documentAmount',
                        label: 'Monto documento',
                      },
                    ]}
                  />
                  <DateInput
                    label="Nueva fecha"
                    value={paymentDate.toISOString()}
                    onChange={(value) => setPaymentDate(dayjs(value))}
                  />
                </Box>
              </PageHeader.ActionsBar>
            </PageHeader>

            <EnhancedTable dataHeaders={headers} data={documents} />
          </div>
        </DialogContent>
        <DialogActions>
          <CustomButton color="primary" onClick={() => setOpen(false)}>
            Salir
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DebtProjector;
